import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import PagePagination from "../../components/PagePagination";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getBasicFilteringOptions,
  getClearBasicModel,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import AddOrEditMeasurementUnit from "./components/AddOrEditMeasurementUnit";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function MeasurementUnitList() {
  const {
    measurementUnitModel,
    setMeasurementUnitModel,
    measurementUnitData,
    MEASUREMENTUNIT_API,
    config,
    pageIndexMeasurementUnit,
    setPageIndexMeasurementUnit,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [measurementUnitDataFiltered, setMeasurementUnitDataFiltered] =
    useState(measurementUnitData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const operationalDataObj = measurementUnitData.Data.find(
        (operationalAreaObj) => operationalAreaObj.Id === Number(id)
      );
      setMeasurementUnitModel(operationalDataObj);
    }
  }, [id]);

  useEffect(() => {
    setMeasurementUnitDataFiltered(measurementUnitData.Data);
  }, [measurementUnitData]);

  const validateOperationResult = (result, isDelete) => {
    setMeasurementUnitModel(getClearBasicModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([MEASUREMENTUNIT_API, config]);
    if (isDelete) {
      if (
        pageIndexMeasurementUnit !== 0 &&
        measurementUnitData.Data.length === 1
      ) {
        setPageIndexMeasurementUnit(pageIndexMeasurementUnit - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${MEASUREMENTUNIT_API}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${MEASUREMENTUNIT_API}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${MEASUREMENTUNIT_API}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !measurementUnitModel?.Name ||
    measurementUnitModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.symbol,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.measurement_units}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={MEASUREMENTUNIT_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="measurement_unit"
        />
      )}

      {showEditModal && (
        <ModalBackground
          hasRequiredValues
          isDrawerMode
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_measurement_unit}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(measurementUnitModel);
            setShowEditModal(false);
            setMeasurementUnitModel(getClearBasicModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setMeasurementUnitModel(getClearBasicModel());
          }}
          Component={
            <AddOrEditMeasurementUnit
              model={measurementUnitModel}
              setModel={setMeasurementUnitModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.measurement_unit}
            subtitle={localize.measurement_unit_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="measurement_unit_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                hasRequiredValues
                isDrawerMode
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_measurement_unit}
                onClickToSubmit={() => {
                  handleCreateRequest(measurementUnitModel);
                  setShowAddModal(false);
                  setMeasurementUnitModel(getClearBasicModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setMeasurementUnitModel(getClearBasicModel());
                }}
                Component={
                  <AddOrEditMeasurementUnit
                    model={measurementUnitModel}
                    setModel={setMeasurementUnitModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={measurementUnitModel.Name}
          entityType={localize.measurement_unit}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setMeasurementUnitModel(getClearBasicModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(measurementUnitModel.Id);
            setShowDeleteModal(false);
            setMeasurementUnitModel(getClearBasicModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexMeasurementUnit}
            setPageIndex={setPageIndexMeasurementUnit}
            dataArray={measurementUnitData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          measurementUnitData.Data?.length !== 0
            ? measurementUnitDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setMeasurementUnitModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setMeasurementUnitModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  <ValueOrNoDataLabel data={item.Name} />,
                  <ValueOrNoDataLabel data={item.Symbol} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
